/**
 * PORTED — RO-3-06 / #44299.
 * Origin: `excellent/app/audits/page.tsx`.
 *
 * Cause A rewrite: the hub's list read (`allSectionAuditStatuses` + self-heal)
 * moves to `sectionAudit.hub`. The run-all control calls
 * `otherActions.auditRunDue` (already mounted by RO-2-11b). Each card is the
 * shared `SectionAuditPanel` (RO-3-00f) which self-loads findings via
 * `sectionAudit.panel`.
 *
 * Mounted under ModePage as `ops.audits` (Operations ▸ Audits domain). The
 * top-level `/audits` path is a shell cascade redirect to
 * `/operations?area=audits` — no client route of its own (same shape as
 * /finance, /team, /directory).
 */

import { PageHeader, EmptyState } from "@excellent/ui";
import { ShieldCheckIcon } from "@excellent/ui/icons";
import { useApi, apiCall } from "@excellent/ui/api";
import { sectionAudit } from "@excellent/ui/api/contracts/section-audit";
import { otherActions } from "@excellent/ui/api/contracts/other-actions";
import { SectionAuditPanel } from "../projects/section-audit-panel";
import { RunAllAuditsButton } from "./run-all-button";

export function AuditsHub() {
  const { data, isLoading, error, mutate } = useApi(sectionAudit.hub, {});

  if (isLoading && !data) {
    return (
      <div className="space-y-8 px-6 py-8">
        <PageHeader
          title="Best-in-class audits"
          description="Check each business function against what best-in-class looks like — run them all, then fix what surfaces."
        />
        <p className="type-body text-muted-foreground">Loading audits…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-8 px-6 py-8">
        <PageHeader
          title="Best-in-class audits"
          description="Check each business function against what best-in-class looks like — run them all, then fix what surfaces."
        />
        <p className="type-body text-destructive">Could not load audit status.</p>
      </div>
    );
  }

  const { workspaceId, sections, dueCount } = data;

  async function onRunAll(): Promise<void> {
    // auditRunDue's response body carries its own `{ok, error?}` (same shape as
    // the Next server action). apiCall only throws on transport/envelope failure.
    const res = await apiCall(otherActions.auditRunDue, {});
    if (!res.ok) throw new Error(res.error ?? "Failed to start audits");
    await mutate();
  }

  return (
    <div className="space-y-8 px-6 py-8">
      <PageHeader
        title="Best-in-class audits"
        description="Check each business function against what best-in-class looks like — run them all, then fix what surfaces."
        toolbar={
          sections.length > 0 ? (
            <RunAllAuditsButton dueCount={dueCount} onRunAll={onRunAll} />
          ) : undefined
        }
      />

      {sections.length === 0 ? (
        <EmptyState
          icon={<ShieldCheckIcon strokeWidth={1.5} className="size-6" />}
          title="No functions ready to audit yet"
          description="Audits unlock once a function's setup is complete. Finish setting up a function and its best-in-class audit will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {sections.map((s) => (
            <SectionAuditPanel
              key={s.section_slug}
              sectionSlug={s.section_slug}
              workspaceId={workspaceId}
              projectId={null}
              projectSlug={null}
              display="card"
            />
          ))}
        </div>
      )}
    </div>
  );
}
